import { Badge, Button, Card, EmptyState } from "@helmr/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { getChecklist, tickItem, type GoliveChecklist, type GoliveItem, type GoliveState } from "../../api/golive";
import { useServices } from "../../app/services";
import { Load, Page, TableSkeleton, fmtDateTime, useScope } from "../settings/kit";
import { section } from "../settings/sections";
import s from "../settings/settings.module.css";
import o from "./onboarding.module.css";
import { isApiError } from "../../api/client";
import { g } from "./goliveStrings";

const TONE: Record<GoliveState, "ok" | "crit" | "neutral"> = { ok: "ok", ticked: "ok", red: "crit", not_started: "neutral" };

/**
 * `/settings/go-live`: the owner's copy of the checklist the Helmr operator works through (founder question 7). Read only,
 * except that someone holding `settings.golive_edit` can tick an item no automatic check can see. The go-live check itself
 * and the reset are the operator's, in the console.
 */
export function GoLivePage() {
  const { client } = useServices();
  const sc = useScope();
  const key = sc.key("golive", "checklist");
  const q = useQuery({ queryKey: key, queryFn: () => getChecklist(client), retry: false });
  const canEdit = sc.ctx.has("settings.golive_edit");
  const put = (c: GoliveChecklist) => sc.qc.setQueryData(key, c);
  const fmt = (ms: number) => fmtDateTime(ms, sc.ctx.timeZone, sc.ctx.locale);

  return (
    <Page title={g.title} description={g.intro} allow={section("go-live")}>
      <Load query={q} what="the checklist" skeleton={<TableSkeleton rows={8} />}>
        {(c) => (
          <>
            <Summary c={c} fmt={fmt} />
            {c.items.length === 0 ? (
              <EmptyState compact icon="clock" title={g.empty.title} description={g.empty.body} />
            ) : (
              <ul className={o.golive} aria-label="Checklist items">
                {c.items.map((i) => <Row key={i.key} i={i} fmt={fmt} canEdit={canEdit && c.liveAt === null} onChange={put} />)}
              </ul>
            )}
            {!canEdit && c.liveAt === null ? <p className={o.note}>{g.readOnly}</p> : null}
          </>
        )}
      </Load>
    </Page>
  );
}

function Summary({ c, fmt }: { c: GoliveChecklist; fmt: (ms: number) => string }) {
  const done = c.items.filter((i) => i.state === "ok" || i.state === "ticked").length;
  return (
    <Card>
      <div className={o.goliveSummary}>
        <div>
          <h2>{c.liveAt ? g.live : g.doneOf(done, c.items.length)}</h2>
          <p>{c.liveAt ? g.liveSince(fmt(c.liveAt)) : c.ready ? g.allGreen : g.leftTitle(c.blocking.length)}</p>
        </div>
        {!c.liveAt ? (
          <div className={o.goliveBar} role="progressbar" aria-label={g.progress} aria-valuemin={0} aria-valuemax={c.items.length} aria-valuenow={done}>
            <div style={{ width: `${c.items.length ? (done / c.items.length) * 100 : 0}%` }} />
          </div>
        ) : null}
      </div>
    </Card>
  );
}

function Row({ i, fmt, canEdit, onChange }: { i: GoliveItem; fmt: (ms: number) => string; canEdit: boolean; onChange: (c: GoliveChecklist) => void }) {
  const { client } = useServices();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const tick = useMutation({ mutationFn: () => tickItem(client, i.key, reason.trim()), onSuccess: (c) => { onChange(c); setOpen(false); setReason(""); } });
  const green = i.state === "ok" || i.state === "ticked";
  return (
    <li className={o.goliveRow} id={`item-${i.key}`}>
      <div><Badge tone={TONE[i.state]} dot>{g.state[i.state]}</Badge></div>
      <div className={o.goliveBody}>
        <div className={o.goliveTitle}><b>{i.label}</b><span>{i.required ? g.required : g.optional}</span></div>
        <p>{i.detail}</p>
        {i.tick ? <p className={o.note}>{g.tickedAt(fmt(i.tick.at), i.tick.reason)}</p> : null}
        {i.data?.terminals && i.data.terminals.length > 0 ? (
          <ul aria-label={g.terminals} className={o.goliveTerminals}>
            {i.data.terminals.map((t) => (
              <li key={t.id}>
                <b>{t.label ?? t.id}</b>: {t.unmapped.length === 0 ? g.noneUnmapped : `${t.unmapped.length} unmapped (${t.unmapped.slice(0, 12).join(", ")}${t.unmapped.length > 12 ? ", …" : ""})`}
                {t.reserved.map((u) => <span key={u}> · user {u}: {g.reserved}</span>)}
              </li>
            ))}
          </ul>
        ) : null}
        {canEdit && i.tickable && !green ? (
          open ? (
            <form className={o.goliveTick} onSubmit={(e) => { e.preventDefault(); if (reason.trim().length >= 3) tick.mutate(); }}>
              <label htmlFor={`tick-${i.key}`}>{g.tickReason}</label>
              <textarea id={`tick-${i.key}`} className={s.input} value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />
              {tick.error ? <div className={s.formErr} role="alert">{isApiError(tick.error) ? tick.error.message : "Something went wrong. Try again."}{isApiError(tick.error) && tick.error.requestId ? ` (request ${tick.error.requestId})` : ""}</div> : null}
              <div className={o.goliveActs}>
                <Button size="sm" variant="primary" type="submit" busy={tick.isPending} disabled={reason.trim().length < 3}>{g.tickSave}</Button>
                <Button size="sm" type="button" onClick={() => { setOpen(false); tick.reset(); }}>{g.tickCancel}</Button>
              </div>
            </form>
          ) : (
            <div className={o.goliveActs}><Button size="sm" aria-label={`${g.tick}: ${i.label}`} onClick={() => setOpen(true)}>{g.tick}</Button></div>
          )
        ) : null}
      </div>
    </li>
  );
}
